import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import {
  getDefaultRouteForRole,
  navigationByRole,
  type NavItem,
} from '@/config/navigation';
import { duration, easing } from '@/lib/motion';
import type { StaffRole } from '@/types';
import {
  useBreadcrumbTailValue,
  type BreadcrumbCrumb,
} from './BreadcrumbTailContext';

type Crumb = BreadcrumbCrumb;

function humanize(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function findBestMatch(
  pathname: string,
  items: NavItem[],
): { item: NavItem; isExact: boolean } | null {
  let best: { item: NavItem; isExact: boolean } | null = null;
  let bestLength = 0;
  items.forEach((item) => {
    if (!item.href) return;
    if (pathname === item.href) {
      best = { item, isExact: true };
      bestLength = item.href.length;
    } else if (
      pathname.startsWith(item.href + '/') &&
      item.href.length > bestLength
    ) {
      best = { item, isExact: false };
      bestLength = item.href.length;
    }
  });
  return best;
}

function buildCrumbs(
  pathname: string,
  role: StaffRole | undefined,
  tail: Crumb[],
): Crumb[] {
  const crumbs: Crumb[] = [];
  const home: Crumb = role
    ? { label: 'Inicio', href: getDefaultRouteForRole(role) }
    : { label: 'Inicio', href: '/' };
  crumbs.push(home);

  if (!role) return crumbs;

  const nav = navigationByRole[role];
  const allItems = [...nav.global, ...nav.country].filter((i) => !i.section);

  const match = findBestMatch(pathname, allItems);

  if (match) {
    // The matched nav item is clickable so the user can pop back to the
    // list view. Previously this crumb was plain text, which gave the
    // user nowhere to go when they were two levels deep in a detail page.
    crumbs.push({ label: match.item.label, href: match.item.href });

    if (tail.length > 0) {
      // A page provided its own tail — prefer that over the URL-derived
      // fallback. This is how dynamic detail pages get a human name
      // (e.g. company/store name) at the end of the breadcrumb instead
      // of either a raw UUID or nothing at all.
      crumbs.push(...tail);
    } else if (!match.isExact && match.item.href) {
      const rest = pathname
        .slice(match.item.href.length)
        .split('/')
        .filter(Boolean);
      // Skip UUID-ish / id-ish segments to avoid "a1b2-c3d4-..." crumbs;
      // keep named segments (e.g. /companies/:id/stores → "Stores").
      const named = rest.filter(
        (s) => s.length > 0 && !/^[a-f0-9-]{20,}$/i.test(s) && !/^\d+$/.test(s),
      );
      if (named.length > 0) {
        const last = named[named.length - 1];
        crumbs.push({ label: humanize(last) });
      }
    }
  } else if (tail.length > 0) {
    // No nav match but the page still wants to render breadcrumbs — fall
    // back to home + tail.
    crumbs.push(...tail);
  } else {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      crumbs.push({ label: humanize(segments[0]) });
    }
  }

  return crumbs;
}

export function Breadcrumbs() {
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.profiles?.staff?.role as StaffRole | undefined;
  const tail = useBreadcrumbTailValue();

  const crumbs = useMemo(
    () => buildCrumbs(location.pathname, role, tail),
    [location.pathname, role, tail],
  );

  if (crumbs.length <= 1) return null;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.nav
        key={location.pathname}
        aria-label="Navegación"
        initial={{ opacity: 0, y: -2 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -2 }}
        transition={{ duration: duration.base, ease: easing.standard }}
        className="flex items-center gap-1.5 text-[12px] text-muted-foreground"
      >
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          const content = (
            <span
              className={
                isLast ? 'font-medium text-foreground' : 'hover:text-foreground transition-colors'
              }
            >
              {crumb.label}
            </span>
          );
          return (
            <span key={idx} className="flex items-center gap-1.5">
              {crumb.href && !isLast ? (
                <Link to={crumb.href} className="cursor-pointer">
                  {content}
                </Link>
              ) : (
                content
              )}
              {!isLast && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-foreground-tertiary"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </span>
          );
        })}
      </motion.nav>
    </AnimatePresence>
  );
}

export default Breadcrumbs;
